import { useRef, useState } from "react";
import { filesApi } from "@/services/api";
import type { Receipt } from "@/models/types";
import { useToast } from "@/store/ToastContext";
import { formatFileSize } from "@/utils/format";

interface ReceiptUploaderProps {
  value: Receipt | null;
  onChange: (receipt: Receipt | null) => void;
}

export default function ReceiptUploader({ value, onChange }: ReceiptUploaderProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [imageError, setImageError] = useState(false);
  const { showToast } = useToast();

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    setUploading(true);
    setImageError(false);
    try {
      const monthId = new Date().toISOString().slice(0, 7); // "YYYY-MM"
      const uploaded = await filesApi.upload(file, monthId);

      const receipt: Receipt = {
        id: uploaded.drive_file_id,
        url: uploaded.drive_url,
        fileName: uploaded.file_name,
        fileSize: uploaded.file_size,
        fileType: uploaded.mime_type.startsWith("image/") ? "image" : "pdf",
      };
      onChange(receipt);
      showToast("Recibo subido correctamente.");
    } catch (error) {
      console.error("Error subiendo archivo:", error);
      showToast("No se pudo subir el archivo.", "error");
    } finally {
      setUploading(false);
    }
  };

  const removeReceipt = () => {
    onChange(null);
    setImageError(false);
  };

  const showAsImage = value?.fileType === "image" && !imageError;

  return (
    <div>
      {value ? (
        <div className="flex items-center gap-3 p-3 border rounded-md bg-background-100">
          {showAsImage ? (
            <img
              src={value.url}
              alt={value.fileName}
              className="w-16 h-16 object-cover rounded-md"
              onError={() => setImageError(true)}
            />
          ) : (
            <span
              className={`w-16 h-16 rounded-md flex items-center justify-center ${
                value.fileType === "image"
                  ? "bg-blue-100 text-blue-500"
                  : "bg-red-100 text-red-500"
              }`}
            >
              <i
                className={
                  value.fileType === "image"
                    ? "ri-image-line text-3xl"
                    : "ri-file-pdf-2-line text-3xl"
                }
              />
            </span>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{value.fileName}</p>
            <p className="text-xs text-foreground-500 uppercase">
              {value.fileType === "image" ? "Imagen" : "PDF"} · {formatFileSize(value.fileSize)}
            </p>
          </div>
          <button
            type="button"
            onClick={removeReceipt}
            className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-secondary-200 text-foreground-600 cursor-pointer"
            title="Eliminar recibo"
          >
            <i className="ri-delete-bin-line" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-secondary-300 rounded-md p-4 text-sm text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer disabled:opacity-50"
        >
          {uploading ? (
            <>
              <i className="ri-loader-4-line animate-spin" />
              Subiendo...
            </>
          ) : (
            <>
              <i className="ri-upload-2-line text-lg" />
              Subir foto o PDF
            </>
          )}
        </button>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,.pdf"
        className="hidden"
        onChange={handleFileSelect}
      />
    </div>
  );
}